import { AxiosResponse } from 'axios';
import api from 'config/api.config';

// ==================== Interfaces ====================

// Super Admin User Interface
export interface SuperAdminUser {
  _id: string;
  email: string;
  fullName: string;
  first_name?: string;
  last_name?: string;
  profile_picture?: string | null;
  subscription: string | {
    name: string;
    tier: number;
  };
  subscription_name?: string;
  expired: boolean;
  created_on: string;
  joiningDate?: string;
  accountsCount: number;
  activeAccountsCount: number;
}

export interface SuperAdminUsersResponse {
  users: SuperAdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SuperAdminUsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string; // active, expired, deleted
  subscription?: string; // Trial, Starter, Pro, Elite
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Super Admin Account Interface
export interface SuperAdminAccount {
  _id: string;
  username: string;
  full_name: string;
  platform: string;
  is_active: boolean;
  is_locked?: boolean;
  lock_title?: { en: string; fr: string };
  lock_description?: { en: string; fr: string };
  locked_at?: string;
  createdAt: string;
  next_job_time: string | null;
  user: {
    _id: string;
    email: string;
    fullName: string;
  };
  totalJobs: number;
  pendingJobs: number;
}

export interface SuperAdminAccountsResponse {
  accounts: SuperAdminAccount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SuperAdminAccountsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string; // active, inactive, deleted
  platform?: string;
  userId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Super Admin Job Interface
export interface SuperAdminJob {
  _id: string;
  status: string;
  job_type: string | string[]; // Can be single string or array
  created_on: string;
  createdAt?: string; // alias for created_on
  scheduled_for?: string | null;
  actioned_before?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
  error?: string | null;
  actions?: Array<{
    action_id?: string;
    action_type: string;
    action_count?: number;
    target?: string;
    status?: string;
    [key: string]: any;
  }>;
  account?: {
    _id: string;
    username: string;
    platform: string;
  };
  user?: {
    _id: string;
    fullName?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
  };
  proxy?: {
    _id: string;
    proxy: string; // Format: "host:port"
    country: string;
    provider: string;
    status: string;
  };
  instance?: {
    _id?: string;
    instance_name?: string;
    status?: string;
    machine_id?: string;
  };
}

export interface SuperAdminJobsResponse {
  jobs: SuperAdminJob[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SuperAdminJobsQueryParams {
  page?: number;
  limit?: number;
  status?: string; // pending, completed, failed, cancelled
  accountId?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Deleted User Interface
export interface DeletedUser {
  _id: string;
  name: string;
  email: string;
  profilePicture: string | null;
  role: string;
  deletedAt: string;
  deletedBy?: string;
  subscription?: {
    type: string;
    status: string;
  };
}

export interface DeletedUsersResponse {
  users: DeletedUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore?: boolean;
  };
}

export interface DeletedUsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
}

// User Delete Response
export interface UserDeleteResponse {
  success: boolean;
  message: string;
  userId?: string;
}

// Bulk Delete Response
export interface BulkDeleteResponse {
  success: boolean;
  message: string;
  deleted: number;
  failed: number;
  errors?: string[];
}

// Account Interface
export interface SocialAccount {
  _id: string;
  username: string;
  platform: string;
  status: string;
  userId: string;
  createdAt: string;
}

// Job Stats Interface
export interface JobStats {
  total: number;
  byStatus: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
  byType?: {
    [key: string]: number;
  };
  oldestJob?: string;
  newestJob?: string;
}

export interface JobStatsResponse {
  success: boolean;
  stats: JobStats;
}

// Clear Jobs Response
export interface ClearJobsResponse {
  success: boolean;
  message: string;
  deletedCount: number;
}

// Execution Logs
export interface ExecutionLog {
  _id: string;
  job_id: string;
  job_status: string;
  level: 'info' | 'warning' | 'error' | 'debug';
  message: string;
  error_message?: string | null;
  timestamp: string;
  created_at: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  account_id?: string;
  account_username?: string;
  platform?: string;
  instance_id?: string;
  instance_name?: string;
  instance_slot?: number;
}

export interface ExecutionLogsResponse {
  success: boolean;
  logs: ExecutionLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface ExecutionLogsQueryParams {
  page?: number;
  limit?: number;
  level?: string;
  job_id?: string;
  account_id?: string;
  user_id?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
}

// ==================== Redis Queue Management Interfaces ====================

// Individual queue info
export interface QueueInfo {
  name: string;
  type: 'default' | 'machine';
  machineHost?: string;
  isPaused: boolean;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused?: number;
  };
  total: number;
}

// List all queues response
export interface QueueListResponse {
  queues: QueueInfo[];
  totalQueues: number;
  totalCounts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
  grandTotal: number;
}

// Queue status response (all queues)
export interface AllQueuesStatusResponse {
  totalQueues: number;
  queues: Array<{
    name: string;
    type: 'default' | 'machine';
    isPaused: boolean;
    counts: {
      waiting: number;
      active: number;
      completed: number;
      failed: number;
      delayed: number;
      paused: number;
    };
  }>;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
    total: number;
  };
  summary: {
    pending: number;
    inProgress: number;
    successful: number;
    failed: number;
  };
}

// Single queue status response
export interface SingleQueueStatusResponse {
  queueName: string;
  isPaused: boolean;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
    total: number;
  };
  summary: {
    pending: number;
    inProgress: number;
    successful: number;
    failed: number;
  };
}

// Queue Job Interface
export interface QueueJob {
  id: string;
  name: string;
  data: {
    job_id?: string;
    [key: string]: any;
  };
  queueName: string;
  timestamp: number;
  processedOn?: number | null;
  finishedOn?: number | null;
  attemptsMade: number;
  failedReason?: string | null;
  delay?: number;
  priority?: number;
}

export interface QueueJobsResponse {
  queueName: string;
  jobs: QueueJob[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  status: string;
}

export interface QueueJobsQueryParams {
  queue?: string;
  status?: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed';
  page?: number;
  limit?: number;
}

// Clear queue response
export interface ClearQueueResponse {
  message: string;
  queueName?: string;
  clearedByQueue?: Record<string, {
    waiting: number;
    completed: number;
    failed: number;
    delayed: number;
    total: number;
  }>;
  clearedByStatus?: {
    waiting: number;
    completed: number;
    failed: number;
    delayed: number;
  };
  totalCleaned: number;
}

export interface ClearQueueParams {
  queue?: string;
  status?: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'paused' | 'all';
  grace?: number;
  limit?: number;
}

// Pause/Resume queue response
export interface PauseResumeResponse {
  message: string;
  queueName?: string;
  pausedQueues?: string[];
  resumedQueues?: string[];
}

// Remove job response
export interface RemoveJobResponse {
  message: string;
  jobId: string;
  queueName: string;
}

// Bulk remove jobs response
export interface BulkRemoveJobsResponse {
  message: string;
  results: {
    removed: Array<{ jobId: string; queueName: string }>;
    notFound: string[];
    failed: string[];
  };
}

// Retry failed response
export interface RetryFailedResponse {
  message: string;
  queueName?: string;
  retriedByQueue?: Record<string, number>;
  retriedCount?: number;
  totalRetried?: number;
}

// Drain queue response
export interface DrainQueueResponse {
  message: string;
  queueName?: string;
  drainedByQueue?: Record<string, { waiting: number; delayed: number }>;
  waitingJobsRemoved?: number;
  delayedJobsRemoved?: number;
  totalWaitingJobsRemoved?: number;
  totalDelayedJobsRemoved?: number;
}

// Legacy interface for backwards compatibility
export interface QueueStatusResponse {
  queueName: string;
  isPaused: boolean;
  counts: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
    paused: number;
    total: number;
  };
  summary: {
    pending: number;
    inProgress: number;
    successful: number;
    failed: number;
  };
}

// Scheduler Status Interface
export interface SchedulerStatusResponse {
  enabled: boolean;
  lastUpdated: string;
  updatedBy: string;
  message: string;
}

// ==================== List API Functions ====================

/**
 * Get all users (Super Admin endpoint)
 * GET /api/admin/super/users
 */
export const getSuperAdminUsers = async (params?: SuperAdminUsersQueryParams): Promise<SuperAdminUsersResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.search) queryParams.search = params.search;
    if (params?.status) queryParams.status = params.status;
    if (params?.subscription) queryParams.subscription = params.subscription;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<SuperAdminUsersResponse> = await api.get(
      `/api/admin/super/users`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch users');
  }
};

/**
 * Get all social media accounts (Super Admin endpoint)
 * GET /api/admin/super/accounts
 */
export const getSuperAdminAccounts = async (params?: SuperAdminAccountsQueryParams): Promise<SuperAdminAccountsResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.search) queryParams.search = params.search;
    if (params?.status) queryParams.status = params.status;
    if (params?.platform) queryParams.platform = params.platform;
    if (params?.userId) queryParams.userId = params.userId;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<SuperAdminAccountsResponse> = await api.get(
      `/api/admin/super/accounts`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch accounts');
  }
};

/**
 * Get all jobs (Super Admin endpoint)
 * GET /api/admin/super/jobs
 */
export const getSuperAdminJobs = async (params?: SuperAdminJobsQueryParams): Promise<SuperAdminJobsResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.status) queryParams.status = params.status;
    if (params?.accountId) queryParams.accountId = params.accountId;
    if (params?.userId) queryParams.userId = params.userId;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<SuperAdminJobsResponse> = await api.get(
      `/api/admin/super/jobs`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch jobs');
  }
};

// ==================== User Management API Functions ====================

/**
 * Get all soft-deleted users (for recovery)
 */
export const getDeletedUsers = async (params?: DeletedUsersQueryParams): Promise<DeletedUsersResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 10,
    };

    if (params?.search) queryParams.search = params.search;

    const response: AxiosResponse<DeletedUsersResponse> = await api.get(
      `/api/admin/super/users/deleted`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch deleted users');
  }
};

/**
 * Delete a user (soft by default, permanent with query param)
 */
export const deleteUser = async (userId: string, permanent: boolean = false): Promise<UserDeleteResponse> => {
  try {
    const response: AxiosResponse<UserDeleteResponse> = await api.delete(
      `/api/admin/super/users/${userId}`,
      {
        params: { permanent }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete user');
  }
};

/**
 * Bulk delete users
 */
export const bulkDeleteUsers = async (userIds: string[], permanent: boolean = false): Promise<BulkDeleteResponse> => {
  try {
    const response: AxiosResponse<BulkDeleteResponse> = await api.delete(
      `/api/admin/super/users/bulk`,
      {
        data: { userIds, permanent }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to bulk delete users');
  }
};

/**
 * Restore a soft-deleted user
 */
export const restoreUser = async (userId: string): Promise<UserDeleteResponse> => {
  try {
    const response: AxiosResponse<UserDeleteResponse> = await api.post(
      `/api/admin/super/users/${userId}/restore`,
      {}
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to restore user');
  }
};

// ==================== Social Media Account Management API Functions ====================

/**
 * Delete a social media account
 */
export const deleteSocialAccount = async (accountId: string, permanent: boolean = false): Promise<UserDeleteResponse> => {
  try {
    const response: AxiosResponse<UserDeleteResponse> = await api.delete(
      `/api/admin/super/accounts/${accountId}`,
      {
        params: { permanent }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete social account');
  }
};

/**
 * Bulk delete social media accounts
 */
export const bulkDeleteSocialAccounts = async (accountIds: string[], permanent: boolean = false): Promise<BulkDeleteResponse> => {
  try {
    const response: AxiosResponse<BulkDeleteResponse> = await api.delete(
      `/api/admin/super/accounts/bulk`,
      {
        data: { accountIds, permanent }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to bulk delete social accounts');
  }
};

// Lock/Unlock Response Interface
export interface LockUnlockResponse {
  message: string;
  matchedCount: number;
  modifiedCount: number;
}

/**
 * Lock social media accounts
 * POST /api/admin/super/accounts/lock
 */
export const lockAccounts = async (data: {
  account_ids: string[] | 'all';
  title: { en: string; fr: string };
  description: { en: string; fr: string };
}): Promise<LockUnlockResponse> => {
  try {
    const response: AxiosResponse<LockUnlockResponse> = await api.post(
      `/api/admin/super/accounts/lock`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to lock accounts');
  }
};

/**
 * Unlock social media accounts
 * POST /api/admin/super/accounts/unlock
 */
export const unlockAccounts = async (data: {
  account_ids: string[] | 'all';
}): Promise<LockUnlockResponse> => {
  try {
    const response: AxiosResponse<LockUnlockResponse> = await api.post(
      `/api/admin/super/accounts/unlock`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to unlock accounts');
  }
};

// ==================== Job Management API Functions ====================

/**
 * Get job statistics by status
 */
export const getJobStats = async (): Promise<JobStatsResponse> => {
  try {
    const response: AxiosResponse<JobStats | JobStatsResponse> = await api.get(
      `/api/admin/super/jobs/stats`
    );

    // Handle both response formats:
    // 1. Direct stats: { total, byStatus, ... }
    // 2. Wrapped stats: { success, stats: { total, byStatus, ... } }
    const data = response.data;
    if ('stats' in data && data.stats) {
      return data as JobStatsResponse;
    }

    // API returns stats directly, wrap it
    const stats = data as JobStats;
    // Ensure processing status exists (API might not include it)
    if (!stats.byStatus.processing) {
      stats.byStatus.processing = 0;
    }
    return {
      success: true,
      stats
    };
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch job stats');
  }
};

/**
 * Clear all jobs (with optional filters)
 */
export const clearAllJobs = async (before?: string): Promise<ClearJobsResponse> => {
  try {
    const response: AxiosResponse<ClearJobsResponse> = await api.delete(
      `/api/admin/super/jobs/all`,
      {
        params: before ? { before } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to clear all jobs');
  }
};

/**
 * Clear jobs by status
 */
export const clearJobsByStatus = async (status: string): Promise<ClearJobsResponse> => {
  try {
    const response: AxiosResponse<ClearJobsResponse> = await api.delete(
      `/api/admin/super/jobs/status/${status}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || `Failed to clear ${status} jobs`);
  }
};

/**
 * Clear jobs for a specific account
 */
export const clearJobsByAccount = async (accountId: string): Promise<ClearJobsResponse> => {
  try {
    const response: AxiosResponse<ClearJobsResponse> = await api.delete(
      `/api/admin/super/jobs/account/${accountId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to clear jobs for account');
  }
};

/**
 * Clear jobs for a specific user
 */
export const clearJobsByUser = async (userId: string): Promise<ClearJobsResponse> => {
  try {
    const response: AxiosResponse<ClearJobsResponse> = await api.delete(
      `/api/admin/super/jobs/user/${userId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to clear jobs for user');
  }
};

/**
 * Fetch execution logs with optional filters
 * GET /api/admin/super/execution-logs
 */
export const getExecutionLogs = async (params?: ExecutionLogsQueryParams): Promise<ExecutionLogsResponse> => {
  try {
    const response: AxiosResponse<ExecutionLogsResponse> = await api.get(
      '/api/admin/super/execution-logs',
      { params }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch execution logs');
  }
};

// ==================== Redis Queue Management API Functions ====================

/**
 * List all queues
 * GET /api/admin/super/queue/list
 */
export const getQueueList = async (): Promise<QueueListResponse> => {
  try {
    const response: AxiosResponse<QueueListResponse> = await api.get(
      `/api/admin/super/queue/list`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch queue list');
  }
};

/**
 * Get queue status (all queues or specific queue)
 * GET /api/admin/super/queue/status?queue=queueName
 */
export const getQueueStatus = async (queueName?: string): Promise<AllQueuesStatusResponse | SingleQueueStatusResponse> => {
  try {
    const response = await api.get(
      `/api/admin/super/queue/status`,
      {
        params: queueName ? { queue: queueName } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch queue status');
  }
};

/**
 * Get queue jobs by status (all queues or specific queue)
 * GET /api/admin/super/queue/jobs?queue=queueName&status=waiting&page=1&limit=20
 */
export const getQueueJobs = async (params?: QueueJobsQueryParams): Promise<QueueJobsResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.queue) queryParams.queue = params.queue;
    if (params?.status) queryParams.status = params.status;

    const response: AxiosResponse<QueueJobsResponse> = await api.get(
      `/api/admin/super/queue/jobs`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch queue jobs');
  }
};

/**
 * Clear Redis queue jobs by status (all queues or specific queue)
 * DELETE /api/admin/super/queue/clear?queue=queueName&status=completed&grace=0&limit=1000
 */
export const clearRedisQueue = async (params?: ClearQueueParams): Promise<ClearQueueResponse> => {
  try {
    const queryParams: any = {};
    if (params?.queue) queryParams.queue = params.queue;
    if (params?.status) queryParams.status = params.status;
    if (params?.grace !== undefined) queryParams.grace = params.grace;
    if (params?.limit !== undefined) queryParams.limit = params.limit;

    const response: AxiosResponse<ClearQueueResponse> = await api.delete(
      `/api/admin/super/queue/clear`,
      {
        params: Object.keys(queryParams).length > 0 ? queryParams : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to clear Redis queue');
  }
};

/**
 * Pause queue (all queues or specific queue)
 * POST /api/admin/super/queue/pause?queue=queueName
 */
export const pauseQueue = async (queueName?: string): Promise<PauseResumeResponse> => {
  try {
    const response: AxiosResponse<PauseResumeResponse> = await api.post(
      `/api/admin/super/queue/pause`,
      {},
      {
        params: queueName ? { queue: queueName } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to pause queue');
  }
};

/**
 * Resume queue (all queues or specific queue)
 * POST /api/admin/super/queue/resume?queue=queueName
 */
export const resumeQueue = async (queueName?: string): Promise<PauseResumeResponse> => {
  try {
    const response: AxiosResponse<PauseResumeResponse> = await api.post(
      `/api/admin/super/queue/resume`,
      {},
      {
        params: queueName ? { queue: queueName } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to resume queue');
  }
};

/**
 * Remove specific job from queue
 * DELETE /api/admin/super/queue/jobs/:jobId?queue=queueName
 */
export const removeQueueJob = async (jobId: string, queueName?: string): Promise<RemoveJobResponse> => {
  try {
    const response: AxiosResponse<RemoveJobResponse> = await api.delete(
      `/api/admin/super/queue/jobs/${jobId}`,
      {
        params: queueName ? { queue: queueName } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to remove job from queue');
  }
};

/**
 * Bulk remove jobs from queue
 * DELETE /api/admin/super/queue/jobs/bulk
 */
export const bulkRemoveQueueJobs = async (jobIds: string[], queueName?: string): Promise<BulkRemoveJobsResponse> => {
  try {
    const response: AxiosResponse<BulkRemoveJobsResponse> = await api.delete(
      `/api/admin/super/queue/jobs/bulk`,
      {
        data: {
          jobIds,
          queue: queueName
        }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to bulk remove jobs from queue');
  }
};

/**
 * Retry failed jobs (all queues or specific queue)
 * POST /api/admin/super/queue/retry-failed?queue=queueName&limit=100
 */
export const retryFailedJobs = async (limit?: number, queueName?: string): Promise<RetryFailedResponse> => {
  try {
    const params: any = {};
    if (limit) params.limit = limit;
    if (queueName) params.queue = queueName;

    const response: AxiosResponse<RetryFailedResponse> = await api.post(
      `/api/admin/super/queue/retry-failed`,
      {},
      {
        params: Object.keys(params).length > 0 ? params : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to retry failed jobs');
  }
};

/**
 * Drain queue (all queues or specific queue)
 * DELETE /api/admin/super/queue/drain?queue=queueName
 */
export const drainQueue = async (queueName?: string): Promise<DrainQueueResponse> => {
  try {
    const response: AxiosResponse<DrainQueueResponse> = await api.delete(
      `/api/admin/super/queue/drain`,
      {
        params: queueName ? { queue: queueName } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to drain queue');
  }
};

// ==================== Scheduler Management API Functions ====================

/**
 * Get scheduler status
 * GET /api/admin/super/scheduler/status
 */
export const getSchedulerStatus = async (): Promise<SchedulerStatusResponse> => {
  try {
    const response: AxiosResponse<SchedulerStatusResponse> = await api.get(
      `/api/admin/super/scheduler/status`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch scheduler status');
  }
};

/**
 * Enable scheduler
 * POST /api/admin/super/scheduler/enable
 */
export const enableScheduler = async (): Promise<SchedulerStatusResponse> => {
  try {
    const response: AxiosResponse<SchedulerStatusResponse> = await api.post(
      `/api/admin/super/scheduler/enable`,
      {}
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to enable scheduler');
  }
};

/**
 * Disable scheduler
 * POST /api/admin/super/scheduler/disable
 */
export const disableScheduler = async (): Promise<SchedulerStatusResponse> => {
  try {
    const response: AxiosResponse<SchedulerStatusResponse> = await api.post(
      `/api/admin/super/scheduler/disable`,
      {}
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to disable scheduler');
  }
};

// ==================== Anbox Cloud Instance Management Interfaces ====================

// Anbox Stats Response
export interface AnboxStatsResponse {
  machines: {
    total: number;
    active: number;
    maintenance: number;
    offline: number;
    total_capacity: number;
  };
  instances: {
    total: number;
    by_status: {
      available: number;
      assigned: number;
      running: number;
      starting: number;
      stopping: number;
      stopped: number;
      error: number;
      maintenance: number;
    };
    used: number;
    utilization_percentage: number;
  };
}

// Anbox Machine Interface
export interface AnboxMachine {
  _id: string;
  name: string;
  host: string;
  port: number;
  api_endpoint?: string;
  total_capacity: number;
  warning_threshold: number;
  critical_threshold: number;
  warning_notification_sent?: boolean;
  critical_notification_sent?: boolean;
  status: 'active' | 'maintenance' | 'offline';
  health_check_status?: 'healthy' | 'unhealthy' | 'unknown';
  priority: number;
  region?: string;
  provider?: string;
  notes?: string;
  created_on?: string;
  created_by?: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
  };
  // Credential flags (actual credentials are not exposed in list/get)
  has_root_credentials?: boolean;
  has_auth_token?: boolean;
  // Computed fields from API
  total_instances?: number;
  available_instances?: number;
  assigned_instances?: number;
  running_instances?: number;
  error_instances?: number;
  used_capacity?: number;
  utilization_percentage?: number;
  instances_by_status?: {
    available: number;
    assigned: number;
    running: number;
    starting: number;
    stopping: number;
    stopped: number;
    error: number;
    maintenance: number;
  };
}

// Anbox Machine Credentials Interface (for sensitive data)
export interface AnboxMachineCredentials {
  machine_id: string;
  name: string;
  host: string;
  port: number;
  api_endpoint?: string;
  root_username?: string;
  root_password?: string;
  auth_token?: string;
}

export interface AnboxMachinesResponse {
  machines: AnboxMachine[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AnboxMachinesQueryParams {
  page?: number;
  limit?: number;
  status?: 'active' | 'maintenance' | 'offline';
  region?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateAnboxMachineRequest {
  name: string;
  host: string;
  port: number;
  api_endpoint?: string;
  auth_token?: string;
  root_username?: string;
  root_password?: string;
  total_capacity: number;
  warning_threshold?: number;
  critical_threshold?: number;
  priority?: number;
  region?: string;
  provider?: string;
  notes?: string;
}

export interface UpdateAnboxMachineRequest {
  name?: string;
  total_capacity?: number;
  warning_threshold?: number;
  critical_threshold?: number;
  priority?: number;
  api_endpoint?: string;
  auth_token?: string;
  root_username?: string;
  root_password?: string;
  region?: string;
  provider?: string;
  notes?: string;
}

// Anbox Instance Interface
export interface AnboxInstance {
  _id: string;
  instance_name: string;
  slot_number: number;
  status: 'available' | 'assigned' | 'starting' | 'running' | 'stopping' | 'stopped' | 'error' | 'maintenance';
  error_message?: string | null;
  last_status_change?: string;
  assigned_at?: string | null;
  anbox_container_id?: string | null;
  anbox_session_id?: string | null;
  ip_address?: string | null;
  last_heartbeat?: string | null;
  consecutive_failures?: number;
  cpu_usage?: number;
  memory_usage?: number;
  last_metrics_update?: string | null;
  machine: {
    _id: string;
    name: string;
    host: string;
    port?: number;
    api_endpoint?: string;
    priority?: number;
  };
  account?: {
    _id: string;
    username: string;
    platform: string;
  } | null;
  user?: {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    fullName?: string;
  } | null;
}

export interface AnboxInstancesResponse {
  instances: AnboxInstance[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AnboxInstancesQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  machineId?: string;
  userId?: string;
  accountId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AvailableInstancesResponse {
  available_count: number;
  instances: AnboxInstance[];
}

export interface AllocateInstanceRequest {
  social_media_account_id: string;
  instance_id?: string;
}

export interface AllocateInstanceResponse {
  message: string;
  instance_id: string;
  instance_name: string;
  machine_id: string;
  social_media_account_id: string;
}

export interface ReleaseInstanceRequest {
  instance_id?: string;
  social_media_account_id?: string;
}

export interface ReleaseInstanceResponse {
  message: string;
  instance_id: string;
  instance_name: string;
}

export interface AddInstancesRequest {
  count: number;
  instance_prefix?: string;
}

export interface AddInstancesResponse {
  message: string;
  machineId: string;
  instancesCreated: number;
  totalInstances: number;
}

// ==================== Anbox Cloud Instance Management API Functions ====================

/**
 * Get Anbox cloud statistics
 * GET /api/admin/super/anbox/stats
 */
export const getAnboxStats = async (): Promise<AnboxStatsResponse> => {
  try {
    const response: AxiosResponse<AnboxStatsResponse> = await api.get(
      `/api/admin/super/anbox/stats`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch Anbox stats');
  }
};

// ==================== Machine Management ====================

/**
 * Create a new Anbox machine
 * POST /api/admin/super/anbox/machines
 */
export const createAnboxMachine = async (data: CreateAnboxMachineRequest): Promise<{ message: string; machine: AnboxMachine }> => {
  try {
    const response = await api.post(
      `/api/admin/super/anbox/machines`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create Anbox machine');
  }
};

/**
 * Get all Anbox machines
 * GET /api/admin/super/anbox/machines
 */
export const getAnboxMachines = async (params?: AnboxMachinesQueryParams): Promise<AnboxMachinesResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.status) queryParams.status = params.status;
    if (params?.region) queryParams.region = params.region;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<AnboxMachinesResponse> = await api.get(
      `/api/admin/super/anbox/machines`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch Anbox machines');
  }
};

/**
 * Get Anbox machine by ID
 * GET /api/admin/super/anbox/machines/:machineId
 */
export const getAnboxMachine = async (machineId: string): Promise<{ machine: AnboxMachine }> => {
  try {
    const response = await api.get(
      `/api/admin/super/anbox/machines/${machineId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch Anbox machine');
  }
};

/**
 * Update Anbox machine
 * PUT /api/admin/super/anbox/machines/:machineId
 */
export const updateAnboxMachine = async (machineId: string, data: UpdateAnboxMachineRequest): Promise<{ message: string; machineId: string }> => {
  try {
    const response = await api.put(
      `/api/admin/super/anbox/machines/${machineId}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update Anbox machine');
  }
};

/**
 * Delete Anbox machine
 * DELETE /api/admin/super/anbox/machines/:machineId
 */
export const deleteAnboxMachine = async (machineId: string, permanent: boolean = false): Promise<{ message: string; machineId: string }> => {
  try {
    const response = await api.delete(
      `/api/admin/super/anbox/machines/${machineId}`,
      {
        params: { permanent }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete Anbox machine');
  }
};

/**
 * Update Anbox machine status
 * PUT /api/admin/super/anbox/machines/:machineId/status
 */
export const updateAnboxMachineStatus = async (machineId: string, status: 'active' | 'maintenance' | 'offline'): Promise<{ message: string; machineId: string; status: string }> => {
  try {
    const response = await api.put(
      `/api/admin/super/anbox/machines/${machineId}/status`,
      { status }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update machine status');
  }
};

/**
 * Add instances to a machine
 * POST /api/admin/super/anbox/machines/:machineId/instances
 */
export const addInstancesToMachine = async (machineId: string, data: AddInstancesRequest): Promise<AddInstancesResponse> => {
  try {
    const response: AxiosResponse<AddInstancesResponse> = await api.post(
      `/api/admin/super/anbox/machines/${machineId}/instances`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to add instances to machine');
  }
};

/**
 * Get instances for a specific machine
 * GET /api/admin/super/anbox/machines/:machineId/instances
 */
export const getMachineInstances = async (machineId: string, params?: { page?: number; limit?: number; status?: string }): Promise<AnboxInstancesResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 50,
    };

    if (params?.status) queryParams.status = params.status;

    const response: AxiosResponse<AnboxInstancesResponse> = await api.get(
      `/api/admin/super/anbox/machines/${machineId}/instances`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch machine instances');
  }
};

// ==================== Instance Management ====================

/**
 * Get all instances across all machines
 * GET /api/admin/super/anbox/instances
 */
export const getAnboxInstances = async (params?: AnboxInstancesQueryParams): Promise<AnboxInstancesResponse> => {
  try {
    const queryParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 50,
    };

    if (params?.status) queryParams.status = params.status;
    if (params?.machineId) queryParams.machineId = params.machineId;
    if (params?.userId) queryParams.userId = params.userId;
    if (params?.accountId) queryParams.accountId = params.accountId;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<AnboxInstancesResponse> = await api.get(
      `/api/admin/super/anbox/instances`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch Anbox instances');
  }
};

/**
 * Get available instances for allocation
 * GET /api/admin/super/anbox/instances/available
 */
export const getAvailableInstances = async (limit?: number): Promise<AvailableInstancesResponse> => {
  try {
    const response: AxiosResponse<AvailableInstancesResponse> = await api.get(
      `/api/admin/super/anbox/instances/available`,
      {
        params: limit ? { limit } : undefined
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch available instances');
  }
};

/**
 * Get instance by ID
 * GET /api/admin/super/anbox/instances/:instanceId
 */
export const getAnboxInstance = async (instanceId: string): Promise<{ instance: AnboxInstance }> => {
  try {
    const response = await api.get(
      `/api/admin/super/anbox/instances/${instanceId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch Anbox instance');
  }
};

/**
 * Update instance details
 * PUT /api/admin/super/anbox/instances/:instanceId
 */
export const updateAnboxInstance = async (instanceId: string, data: { instance_name?: string; anbox_container_id?: string; ip_address?: string }): Promise<{ message: string; instanceId: string }> => {
  try {
    const response = await api.put(
      `/api/admin/super/anbox/instances/${instanceId}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update Anbox instance');
  }
};

/**
 * Update instance status
 * PUT /api/admin/super/anbox/instances/:instanceId/status
 */
export const updateAnboxInstanceStatus = async (instanceId: string, status: string, error_message?: string): Promise<{ message: string; instanceId: string; status: string }> => {
  try {
    const response = await api.put(
      `/api/admin/super/anbox/instances/${instanceId}/status`,
      { status, error_message }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update instance status');
  }
};

/**
 * Delete instance
 * DELETE /api/admin/super/anbox/instances/:instanceId
 */
export const deleteAnboxInstance = async (instanceId: string): Promise<{ message: string; instanceId: string }> => {
  try {
    const response = await api.delete(
      `/api/admin/super/anbox/instances/${instanceId}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to delete Anbox instance');
  }
};

// ==================== Instance Allocation ====================

/**
 * Allocate an instance to a social media account
 * POST /api/admin/super/anbox/instances/allocate
 */
export const allocateInstance = async (data: AllocateInstanceRequest): Promise<AllocateInstanceResponse> => {
  try {
    const response: AxiosResponse<AllocateInstanceResponse> = await api.post(
      `/api/admin/super/anbox/instances/allocate`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to allocate instance');
  }
};

/**
 * Release an instance from a social media account
 * POST /api/admin/super/anbox/instances/release
 */
export const releaseInstance = async (data: ReleaseInstanceRequest): Promise<ReleaseInstanceResponse> => {
  try {
    const response: AxiosResponse<ReleaseInstanceResponse> = await api.post(
      `/api/admin/super/anbox/instances/release`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to release instance');
  }
};

/**
 * Get decrypted machine credentials (sensitive)
 * GET /api/admin/super/anbox/machines/:machineId/credentials
 */
export const getMachineCredentials = async (machineId: string): Promise<AnboxMachineCredentials> => {
  try {
    const response: AxiosResponse<AnboxMachineCredentials> = await api.get(
      `/api/admin/super/anbox/machines/${machineId}/credentials`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch machine credentials');
  }
};
